<script lang="ts">
  import CategoryCard from '$components/category-card.svelte';
  import SectionTitle from '$components/section-title.svelte';
  import SliderButton from '$components/ui/slider-button.svelte';

  import { storyblokEditable } from '$lib/actions/storyblok-editable';
  import { t } from '$lib/i18n';
  import { categories } from '$lib/stores/content';
  import { cn } from '$lib/utils';
  import { getImageAttributes } from '$lib/utils/cms';
  import { buildPath } from '$lib/utils/paths';

  import type { CategoriesCrossSellStoryblok, ShopPageStoryblok } from '$types/bloks';
  import type { ISbStoryData } from '@storyblok/js';

  export let block: CategoriesCrossSellStoryblok;

  const blockCategories = ($categories.filter((c) => block.categories.includes(c.uuid)) || []).sort(
    (a, b) =>
      block.categories.indexOf(a.uuid) - block.categories.indexOf(b.uuid) ||
      a.uuid.localeCompare(b.uuid)
  ) as ISbStoryData<ShopPageStoryblok>[];

  let containerWidth = 0;
  let screenSize = 0;
  let scroll: HTMLDivElement;
  const SCROLL_BY = 300;
  $: scrollLeftMax = scroll?.scrollWidth;
  $: scrollLeft = 0;
</script>

<svelte:window bind:innerWidth={screenSize} />
<section class="py-12 lg:py-16">
  <div
    class={cn(
      'flex w-full 2xl:container mx-auto justify-between items-center px-2 lg:mx-0 2xl:mx-auto lg:px-8 2xl:px-0 gap-4',
      block.title[0].variant === 'left' ? 'flex-row' : 'flex-col'
    )}
    bind:clientWidth={containerWidth}
  >
    <div class="w-fit" use:storyblokEditable={block}>
      <SectionTitle block={block.title[0]} />
    </div>
    <div
      class={cn('flex mb-4 justify-end', block.title[0].variant === 'left' ? 'w-fit' : 'w-full')}
    >
      <!-- Slider Buttons-->
      {#if blockCategories.length > 1}
        <div class="hidden lg:flex gap-2 px-2 lg:px-0">
          <SliderButton
            aria-label={$t('a11y.image-previous')}
            icon="chevron-left"
            variant="ghost"
            disabled={scrollLeft === 0}
            on:click={() => {
              scroll?.scrollBy({ left: -SCROLL_BY, behavior: 'smooth' });
            }}
          />
          <SliderButton
            aria-label={$t('a11y.image-next')}
            icon="chevron-right"
            variant="ghost"
            disabled={scrollLeftMax === scrollLeft}
            on:click={() => {
              scroll?.scrollBy({ left: SCROLL_BY, behavior: 'smooth' });
            }}
          />
        </div>
      {/if}
      <!-- END Slider Buttons -->
    </div>
  </div>
  <div
    class="flex gap-2 overflow-auto hide-scrollbar lg:gap-4"
    style:--x-margin={`${(screenSize - containerWidth) / 2 || 16}px`}
    bind:this={scroll}
    on:scroll={() => {
      scrollLeftMax = scroll?.scrollWidth - screenSize;
      scrollLeft = scroll.scrollLeft;
    }}
  >
    {#each blockCategories as c, i}
      {#if c.content.cover_image?.filename}
        {@const { src, alt } = getImageAttributes(c.content.cover_image, { size: [512, 0] })}
        <CategoryCard
          size="lg"
          label={c.name}
          href={$buildPath({
            path: c.full_slug
          })}
          image={{ src, alt }}
          class={cn(
            'h-[280px] min-w-[200px] lg:min-w-[420px]',
            i === 0 && 'ml-4 lg:ml-8 2xl:ml-[--x-margin]',
            i === blockCategories.length - 1 && 'mr-4 lg:mr-8 2xl:mr-[--x-margin]'
          )}
        />
      {/if}
    {/each}
  </div>
</section>
